"use client";

import { resizeImageUrl } from "@lib/util/images";
import { StoreProductCategory } from "@medusajs/types";
import { clx } from "@medusajs/ui";
import PlaceholderImage from "@modules/common/icons/placeholder-image";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useReducer, useRef, useState } from "react";
import styles from "./hero.module.css";

type DragState = {
  isDragging: boolean;
  startX: number;
  scrollLeft: number;
};

function CategoriesHero({ productCategories }: { productCategories: StoreProductCategory[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const [dragState, dispatch] = useReducer(
    (state: DragState, action: Partial<DragState>) => ({ ...state, ...action }),
    { isDragging: false, startX: 0, scrollLeft: 0 }
  );

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;
    dispatch({
      isDragging: true,
      startX: e.pageX - scrollRef.current.offsetLeft,
      scrollLeft: scrollRef.current.scrollLeft,
    });
  };

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!dragState.isDragging || !scrollRef.current) return;
      e.preventDefault();
      const x = e.pageX - scrollRef.current.offsetLeft;
      const walk = (x - dragState.startX) * 2;
      scrollRef.current.scrollLeft = dragState.scrollLeft - walk;
    },
    [dragState]
  );

  const handleMouseUpOrLeave = () => dispatch({ isDragging: false });

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  return (
    <div className="w-full bg-[#ff0c5d] lg:rounded-2xl group h-fit">
      {/* <div
        onClick={handleScrollLeft}
        className="absolute top-1/2 left-8 bg-slate-500 text-white p-1 rounded-xl -translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover:opacity-100 cursor-pointer xl:hidden inline-block"
      >
        <ChevronLeft />
      </div> */}
      <div
        ref={scrollRef}
        className={clx(
          "overflow-x-auto rounded-2xl cursor-grab active:cursor-grabbing select-none lg:p-3 p-2",
          styles.hideScrollbar
        )}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUpOrLeave}
        onMouseLeave={handleMouseUpOrLeave}
        role="list"
      >
        <div className="grid grid-rows-2 lg:gap-3 gap-2 min-w-max grid-cols-5 justify-items-center">
          {productCategories.map(({ metadata, handle, name }, index) => {
            const thumbnail = metadata?.thumbnail as string | undefined;
            const categoryHref = `/categories/${handle}`;
            return (
              <Link
                key={index}
                href={categoryHref}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="flex flex-col lg:flex-row lg:w-[190px] justify-center lg:justify-start w-[80px] bg-white rounded-2xl items-center p-1 transition-shadow duration-300 hover:shadow-lg lg:py-3 py-2"
                role="listitem"
              >
                <div className="rounded-md lg:w-[40px] lg:h-[40px] w-[30px] h-[30px] overflow-hidden">
                  {thumbnail ? (
                    <Image
                      src={resizeImageUrl(thumbnail, 40, 40)}
                      alt={`Thumbnail ${name}`}
                      width={40}
                      height={40}
                      loading="lazy"
                      className={`lg:w-[40px] lg:h-[40px] w-[30px] h-[30px] ${
                        hoveredIndex === index ? "scale-125 transition-all" : ""
                      }`}
                    />
                  ) : (
                    <PlaceholderImage size={40} className="w-[30px] lg:w-[40px]" color="gray" />
                  )}
                </div>
                <div className="flex flex-col lg:pl-2 px-1">
                  <span
                    className={`lg:text-[14px] text-[12px] lg:font-medium lg:line-clamp-1 line-clamp-1 transition-colors duration-300 ${
                      hoveredIndex === index ? "text-primary-600" : ""
                    }`}
                  >
                    {name}
                  </span>
                  {/*<span className="text-[12px] lg:block hidden">{productsCount} sản phẩm</span>*/}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      {/* <div
        onClick={handleScrollRight}
        className="absolute top-1/2 right-8 bg-slate-500 text-white p-1 rounded-xl -translate-y-1/2 opacity-0 transition-opacity duration-200 group-hover:opacity-100 cursor-pointer xl:hidden inline-block"
      >
        <ChevronRight />
      </div> */}
    </div>
  );
}

export default CategoriesHero;
